import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Field, Input, Textarea, LoadingState, Badge } from "@/components/design-system";
import { CATEGORIES, CATEGORY_LABEL, createProduct, getOwnProduct, updateProduct, uploadLogo, publishProduct, unpublishProduct } from "@/lib/products";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");

function CategorySelect({ value, onChange, id }) {
  return (
    <select
      id={id}
      data-testid="product-category"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#0B1F3A]/20 rounded-lg px-4 py-3 bg-white text-[#0B1F3A] focus:ring-2 focus:ring-[#2D7FF9] focus:border-transparent outline-none"
    >
      <option value="" disabled>Choose a category…</option>
      {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
    </select>
  );
}

export default function ProductForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    tagline: "",
    description: "",
    category: "",
    website_url: "",
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const p = await getOwnProduct(id);
        setProduct(p);
        setForm({
          name: p.name || "",
          tagline: p.tagline || "",
          description: p.description || "",
          category: p.category || "",
          website_url: p.website_url || "",
        });
      } catch (err) { setError(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const disabled = !form.name || !form.tagline || form.description.length < 20 || !form.category;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (isEdit) {
        const next = await updateProduct(id, form);
        setProduct(next);
      } else {
        const next = await createProduct(form);
        nav(`/app/products/${next.id}`, { replace: true });
      }
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !product) return;
    setSaving(true);
    try {
      const next = await uploadLogo(product.id, file);
      setProduct(next);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); e.target.value = ""; }
  };

  const onPublishToggle = async () => {
    if (!product) return;
    setSaving(true);
    try {
      const next = product.status === "published"
        ? await unpublishProduct(product.id)
        : await publishProduct(product.id);
      setProduct(next);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <LoadingState label="Loading product…" />;

  return (
    <div data-testid="product-form-page" className="max-w-3xl">
      <div className="mb-8 flex items-center gap-3">
        <Link to="/app/products" className="text-sm text-[#2D7FF9] font-medium">← Back to products</Link>
      </div>
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">{isEdit ? "Edit product" : "Add product"}</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
        {isEdit ? (product?.name || "Product") : "Add a new product"}
      </h1>
      {product && (
        <div className="mt-3 flex items-center gap-3">
          <Badge variant={product.status === "published" ? "active" : "neutral"}>{product.status}</Badge>
          {product.slug && <span className="text-xs text-[#0B1F3A]/60">/p/{product.slug}</span>}
        </div>
      )}

      <Card className="mt-8">
        <form onSubmit={onSubmit} className="space-y-5">
          <Field label="Product name" htmlFor="p-name" hint="Between 2 and 80 characters.">
            <Input id="p-name" data-testid="product-name" required minLength={2} maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Launchpad Notes" />
          </Field>
          <Field label="One-line tagline" htmlFor="p-tag" hint="What does it do in one sentence?">
            <Input id="p-tag" data-testid="product-tagline" required minLength={4} maxLength={140} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="AI-powered notes for founders" />
          </Field>
          <Field label="Description" htmlFor="p-desc" hint={`${form.description.length}/4000 · minimum 20`}>
            <Textarea id="p-desc" data-testid="product-description" required minLength={20} maxLength={4000} rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What problem does it solve? Who is it for?" />
          </Field>
          <Field label="Category" htmlFor="p-cat">
            <CategorySelect id="p-cat" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
          </Field>
          <Field label="Website URL" htmlFor="p-url" hint="Required — must start with https:// and lead to a live, safe website.">
            <Input id="p-url" data-testid="product-website" type="url" required minLength={1} maxLength={200} placeholder="https://…" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} />
          </Field>
          {error && <p data-testid="product-error" className="text-sm font-medium text-red-600">{error}</p>}
          <div className="flex items-center gap-3">
            <Button data-testid="product-save" type="submit" variant="primary" disabled={disabled || saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create product"}
            </Button>
            <Link to="/app/products"><Button type="button" variant="ghost">Cancel</Button></Link>
          </div>
        </form>
      </Card>

      {isEdit && product && (
        <>
          <Card className="mt-6">
            <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Product logo</h2>
            <p className="text-sm text-[#0B1F3A]/70 mt-1">PNG, JPG, WebP or SVG. Max 2 MB. Square works best.</p>
            <div className="mt-4 flex items-center gap-4">
              {product.logo_url ? (
                <img src={product.logo_url} alt="" className="w-24 h-24 rounded-xl object-cover border border-[#0B1F3A]/10 bg-white" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-[#E8F1FF] border border-[#0B1F3A]/10 flex items-center justify-center text-[#2D7FF9] font-[Montserrat] font-extrabold text-3xl">
                  {(product.name || "?").trim().charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input ref={fileInputRef} data-testid="product-logo-file" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogo} className="hidden" />
                <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={saving} data-testid="product-logo-upload">
                  {product.logo_url ? "Replace logo" : "Upload logo"}
                </Button>
                <span className="text-xs text-[#0B1F3A]/60">Uploads are stored in EU (Spain, eu-west-1).</span>
              </div>
            </div>
          </Card>

          <Card className="mt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Visibility</h2>
                <p className="text-sm text-[#0B1F3A]/70 mt-1">
                  {product.status === "published"
                    ? "This product is live. Anyone can view its public page."
                    : "This product is currently in draft. Publish to open it to the community."}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {product.status === "published" && (
                  <a href={`/p/${product.slug}`} target="_blank" rel="noreferrer">
                    <Button variant="ghost">View public page ↗</Button>
                  </a>
                )}
                <Button
                  data-testid="product-publish"
                  variant={product.status === "published" ? "secondary" : "primary"}
                  onClick={onPublishToggle}
                  disabled={saving}
                >
                  {product.status === "published" ? "Unpublish" : "Publish"}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
